// FP 간트 공용 상수
//
// 간트 3화면(OrderGantt / ResourceGantt / AdjustmentGantt)이 공유한다.
// 구현 방침·근거는 `saas-fp/docs/fp-frontend-migration/reports/gantt-implementation.md`.
// - 라이브러리 도입 없음(canvas/DOM 직접 렌더), 시간축 계산은 date-fns, d3 미사용(§3)
// - 여기엔 "값"만 둔다. 계산은 timelineScales.js, 렌더는 화면별 컴포넌트.

// 헤더 시간 단위 — old `FP_GANTT_BUCKET` 공통코드(HOUR/DAY/WEEK/MONTH)에 대응한다.
// old는 이 목록을 DB(common/search-option/data)에서 읽고 없으면 상수 목록으로 폴백했는데,
// 실측 결과가 고정 4종이라 Report 카테고리의 정적 카탈로그 원칙(fp-report-screen-guide.md §A-3b)에
// 맞춰 화면 상수로 고정했다.
// pxPerUnit = 줌 1배일 때 그 단위 1개가 차지하는 픽셀. 시간축은 선형(pxPerMs)이므로 월 단위는
// 평균 길이 기준이고, 실제 월 경계 폭은 달마다 조금씩 다르게 그려진다(정상 동작).
// 폭 기준값은 old 실행 화면 캡처에 맞췄다 — old Day 단위는 하루가 400px 이상이라 몇 시간짜리
// 활동도 라벨이 들어가는 박스로 보였다(초기값 64px/일로는 6시간 활동이 16px 슬라이버가 된다).
export const HEADER_UNITS = [
  { value: "Hour", labelKey: "FP_HOUR", unitHours: 1, pxPerUnit: 22 },
  { value: "Day", labelKey: "FP_DAY", unitHours: 24, pxPerUnit: 240 },
  { value: "Week", labelKey: "FP_WEEK", unitHours: 24 * 7, pxPerUnit: 220 },
  { value: "Month", labelKey: "FP_MONTH", unitHours: 24 * 30.4375, pxPerUnit: 200 }
];

export const DEFAULT_HEADER_UNIT = "Day";

// 가로 줌(old FP_WIDTH_ZOOM_IN/OUT) — 배율 곱셈. old는 GanttWidth에 고정값을 더했지만, 단위가
// 바뀌면 기준 폭도 달라져 배율 방식이 더 안정적이다.
export const WIDTH_ZOOM_STEP = 1.25;
export const WIDTH_ZOOM_MIN = 0.4;
export const WIDTH_ZOOM_MAX = 6;

// 세로 줌(old FP_ZOOM_IN/OUT = GanttHeight ±8) — 그리드 rowHeight로 대응한다.
// 기본값 32는 **old 실행 화면 실측**이다(2026-08-19 사용자 캡처: 행 간격 32px. 그 캡처는 시간
// 눈금이 23.5px/h로 우리 Hour 단위 22px와 맞아 사실상 1:1 스케일임이 확인됐다). 처음엔
// ag-theme-balham 기본값 28을 그대로 썼는데, 바(0.62×28≈17px) 안에 라벨을 넣으면 밑변 노치까지
// 겹쳐 답답했다 — old가 행을 4px 더 쓰고 있었던 것이다.
export const ROW_HEIGHT_DEFAULT = 32;
export const ROW_HEIGHT_MIN = 20;
export const ROW_HEIGHT_MAX = 76;
export const ROW_HEIGHT_STEP = 8;

// 2단 시간 헤더(위=연/월 병합, 아래=일/시 등) 높이.
//
// 참고값: 앱 그리드는 `DefaultGridSetting.js`의 `headerHeight: 40`이고 `groupHeaderHeight`를 지정하지
// 않으므로(AG Grid 기본 규칙상 그룹 행도 `headerHeight`를 쓴다) **그룹 헤더 화면의 2단 헤더는 80px**다.
// 그대로 맞춰봤으나 **간트에서는 과하다는 판단**(2026-08-19 사용자) — 기본 행(32px) 2.5개를 헤더가
// 먹는다. 그래서 상단 24 + 하단 32 = 56px로 줄였는데, **두 단의 높이가 달라 계단처럼 보였다**
// (2026-08-21 사용자 지적). 2026-08-21부터 **두 단을 같은 높이(둘 중 큰 값 32px)로 맞춘다** → 64px:
//   - 하단 32 : "20(목)"처럼 날짜+요일이 들어가고 세로 격자와 만나는 칸이라 여유가 필요하다(행 높이와 동일)
//   - 상단 32 : 내용은 "2026-08" 한 조각뿐이지만 **아래 단과 같은 높이**여야 2단이 한 덩어리로 읽힌다
// **글자 크기·굵기(12px / 600)는 그리드와 계속 같다** — 사용자 지시는 "크기 위주로 맞추되 높이는 줄여라"였다.
// 이 값은 그리드 80px(40+40)과 의도적으로 다르다. 되돌리려면 32 → 40, 64 → 80.
//
// ⚠️ 상단 높이는 `TIMELINE_HEADER_HEIGHT`의 **정확히 절반**이어야 한다 — 하단 눈금은
// `top: TIMELINE_HEADER_TOP_HEIGHT`에서 시작해 `bottom: 0`까지 늘어나는 구조라, 둘이 어긋나면
// 하단 칸 높이가 조용히 달라진다.
export const TIMELINE_HEADER_TOP_HEIGHT = 32;
export const TIMELINE_HEADER_HEIGHT = 64;

// ── 자체 프레임 UI 토큰 — 테마 "FP Production" (2026-08-19 사용자 확정) ────────────────
// 시안 `prototypes/gantt-theme-styles.html` 테마 6. 선정 근거·제약(격자 필수 / 휴무일 배경 예정 /
// 고채도 짧은 바 / 옅은 의존선)은 reports/gantt-implementation.md §7-2·§7-2a.
// **좌측 pane·헤더의 색은 전부 여기 있다** — 컴포넌트에 색을 직접 쓰지 말 것(테마 교체가 다시 불가능해진다).
export const UI = {
  // ── 헤더 ──────────────────────────────────────────────────────────────────
  // **색·테두리는 간트 테마("FP Production") 그대로**, **치수(글자 크기·굵기, 높이)만 앱 그리드와
  // 맞춘다**(2026-08-19 사용자 지시: "배경색 등은 원래대로, 크기 위주로 맞춰줘").
  //   - 글자: balham `--ag-font-size: 12px` + `.ag-header-row { font-weight: 600 }` ← 크기·굵기만 차용
  //   - 높이: 위 `TIMELINE_HEADER_*`(그리드 그룹 헤더와 동일한 40 + 40)
  //   - 색  : 아래 값들은 테마 고유(그리드의 #f5f7f7 / #bdc3c7를 쓰지 않는다)
  // **헤더 면 = 브랜드(teal) 아주 옅은 틴트**(2026-08-24 사용자 선택, 시안
  // `prototypes/gantt-header-separation.html` H안). 이전 `#fbfdfe`는 흰 본문과 명암비가 **1.01**로
  // 사실상 같은 색이라 헤더와 콘텐츠가 구분되지 않았다 — §7-2j에서 청록 2px 액센트를 걷어낼 때
  // **색을 빼면서 세기까지 같이 빠진** 것이다.
  //
  // `rgba`가 아니라 **흰 배경에 teal 7%를 미리 합성한 불투명 값**을 쓴다(`#048ba8` × 0.07 over white).
  // 헤더 Box는 본문 위에 절대배치되므로 반투명이면 아래 내용이 비칠 위험이 있다.
  //
  // ⚠️ **면만 브랜드고 하단 선은 무채다**(`headerEdge`) — 청록은 `headerAccent` 주석대로
  // **상호작용·상태 전용**(활성 탭 밑줄 / 검색 일치 자원 레일 / 포커스 링 / 검색 배지)이라,
  // 상시 구조선까지 청록으로 두면 한 화면에서 넷이 경쟁하며 색의 의미가 닳는다(시안 G안에서 확인).
  headerBg: "#edf7f9",
  // 헤더 ↔ 본문 경계. `paneSepStrong`과 같은 값이다 — 둘 다 "판이 갈리는 자리"라 같은 세기를 쓴다
  // (한쪽은 세로 경계, 이쪽은 가로 경계). 용도가 달라 토큰은 나눠 둔다.
  headerEdge: "#c3ced6",
  headerFg: "#4a5b66",
  headerFgDim: "#7a8b96",
  headerFontSize: "12px",               // ← 그리드와 같은 크기
  headerFontWeight: 600,                // ← 그리드와 같은 굵기
  // 앱 primary(teal). **상호작용·상태 전용**이다 — 포커스 링 / 로딩 바 / 검색 배지 / 검색 일치 자원
  // 레일. 항상 켜져 있는 구조선(헤더 하단 등)에는 쓰지 않는다 — 그러면 색의 의미가 닳고 상태 표시와
  // 경쟁한다(2026-08-21 헤더 하단을 1px 회색으로 내린 이유, §7-2j·§7-9).
  headerAccent: "#048ba8",
  // 구분선 3단 — **구역 > 컬럼 > 행** 순으로 약해진다. 세 값 모두 DOM(헤더·좌측 pane) 전용이고,
  // 캔버스 격자는 따로다(`TIMELINE_*LINE_COLOR`, 밀도 때문에 다른 위계를 쓴다 §7-2a ①).
  paneSep: "#dfe6ea",   // 구역 경계 — 헤더 ↔ 본문, 월 눈금 등 "약한" 구역 경계
  // **좌측 고정 컬럼 ↔ 타임라인 경계 전용**(2026-08-21). 이전에는 위 `paneSep`(#dfe6ea)을 썼는데
  // 컬럼선 `colLine`(#e9eef1)과 명도 차가 거의 없어 **어디까지가 고정 영역인지 구분되지 않았다**(사용자 지적).
  // 이 경계는 "선 하나"가 아니라 **판이 갈리는 자리**다 → 진한 1px + 오른쪽으로 짧은 그림자.
  // 그림자는 AG Grid가 고정(pinned) 컬럼에 쓰는 관용구와 같다 — 선을 두껍게 하는 것보다 가볍고,
  // "타임라인이 고정 영역 아래로 지나간다"는 깊이 신호를 준다.
  // 세기는 **한 단 낮춘 값**이다(2026-08-21 "너무 강조된 것 같다"). 흰 배경 대비로 보면
  //   colLine 1.17 / paneSep 1.26 / **#c3ced6 1.60** / #aebbc5 1.96(1차 시도, 과했다).
  // 1.6이면 컬럼선(1.17)과는 확실히 갈리고 "판 경계"로는 조용하다. 그림자도 blur·알파를 함께 낮춰
  // 선과 그림자가 같은 비율로 약해지게 했다(한쪽만 낮추면 다시 성격이 갈린다).
  paneSepStrong: "#c3ced6",
  paneSepShadow: "1px 0 3px rgba(15, 23, 42, 0.045)",
  // 컬럼 경계. **`#e9eef1` → `#e0e7ec`로 한 단 올렸다**(2026-08-21 사용자 지적: 헤더 하단 액센트를
  // 없애고 나니 컬럼 간 경계가 너무 흐리다). 옛 값은 `rowLine`과 거의 붙어 있어서 3단 위계가
  // 사실상 2단이었다. 이 값은 헤더 컬럼·눈금 경계와 좌측 pane 컬럼 경계에 함께 걸린다.
  colLine: "#e0e7ec",
  rowLine: "#eef2f5",   // 행 경계 — 가장 약하게(행이 수백 개라 진하면 격자만 보인다)
  // ── 우측 표시 컨트롤 묶음의 면(surface) ──────────────────────────────────
  // 조회조건 줄 맨 끝에는 `FilterContainer`가 **조회조건 접기 버튼**을 직접 붙인다(공용 컴포넌트,
  // 수정 대상 아님). 그 버튼이 간트 표시 버튼들과 같은 색·크기·간격으로 일렬에 놓여 **다섯 번째
  // 간트 버튼처럼 읽혔다**(2026-08-24 사용자 지적). 여백·구분선은 "끝"을 암시할 뿐이어서,
  // 간트 묶음을 **면으로 감싸** 포함/제외를 형태로 확정한다(§7-15, 시안 C안).
  // 배경은 흰 배경과 겨우 구분되는 세기다 — 조회조건 줄에서 이 묶음이 튀면 안 된다.
  ctrlGroupBg: "#f7f9fb",
  ctrlGroupLine: "#e3e9ee",
  cellFontSize: "12.5px",
  cellFg: "#22313a",
  cellFgDim: "#64757f",
  rowHover: "rgba(4, 139, 168, 0.05)",
  rowSelected: "rgba(4, 139, 168, 0.12)",
  // 대체자원 강조(선택한 바의 공정을 수행할 수 있는 다른 자원) — old `.altResource`는 `#0000aa` +
  // bold이었다. raw blue라 앱 어디에도 없는 색이고 자원 코드가 링크처럼 보였다(2026-08-21 사용자 지적).
  // **무채색 면 + 굵기**로 바꿘다(시안 `prototypes/gantt-alt-resource-styles.html` K안):
  //   이 화면에서 teal은 **상호작용·상태**(선택 행 / 검색 일치 레일 / 포커스 링)가 쓰고 채도는 바가 쓴다.
  //   대체자원은 "지금 고른 것과의 **관계**"이지 상태가 아니므로 위계상 색을 쓰지 않는 게 맞고,
  //   덕분에 선택 행(teal 12%)·검색 레일(teal 3px)과 **겹쳐도 각각 읽힌다**(시안에서 겹침 케이스 확인).
  altResourceBg: "rgba(15, 23, 42, 0.055)",
  // 펼침 표식(셰브론) 색은 계층별로 `cellFg`(최상위) / `cellFgDim`(하위)를 그대로 쓴다 — 별도 토큰을
  // 두지 않는다(2026-08-19 셰브론 교체). 말단 행은 글리프 없이 빈 자리만 두므로 점 색도 필요 없다.
  // 주말 눈금 칸 — **반투명이어야 한다.** 헤더 면이 브랜드 틴트(`headerBg`)로 바뀌면서, 불투명
  // `#f2f5f7`을 쓰면 주말 칸만 그 틴트를 **덮어** 회색 패치처럼 떠 보인다(2026-08-24).
  // 반투명이면 헤더 면 위에 한 단 어둡게 얹히므로 어떤 헤더 톤에서도 같은 관계가 유지된다.
  // 이 칸은 불투명한 헤더 Box **안**에 있어 아래 내용이 비칠 위험은 없다.
  weekendHeaderBg: "rgba(15, 23, 42, 0.05)",
  holidayHeaderBg: "#eef1f4",

  // ── 툴팁(GanttTooltip) ────────────────────────────────────────────────────
  // 2026-08-21 점검에서 정리한 것: 툴팁이 자기 색·크기를 따로 갖고 있어 **같은 화면에서 "지연"이
  // 두 색**(바 마크 #e03131 / 툴팁 #d9480f)이었고, 값 색도 캔버스(cellFg, 청회색) 대신 MUI 기본
  // 무채색(#212121)이라 나란히 보면 탁했다. 여기로 모은다.
  //
  // **글자 크기는 3단계만 쓴다** — 코드 / 본문 / 부가. 11·11.5·12를 섞으면 위계로 읽히지 않고
  // 정렬이 어긋난 것처럼 보인다. 본문은 `cellFontSize`(12.5px)와 같아야 캔버스 바 라벨과 어긋나지 않는다.
  tipCodeFontSize: 15.5,                // 식별 코드 = 가장 중요한 한 줄. 본문과 3px 차이
  tipBodyFontSize: 12.5,                // = cellFontSize
  tipSmallFontSize: 11.5,               // 종류 캡션 + 부가 그룹
  // 최상단 밴드 음영. 무채색(#f1f4f7)이었는데 "화면에 회색·흰색이 많아 구분이 안 된다"(2026-08-21 사용자)
  // → **앱 primary(#048BA8) 9% 틴트**로 바꿨다. 신규 색이 아니라 간트 헤더 하단 2px 액센트·포커스 링과 같은
  // 색이고, 서버 `displayColor`가 연한 **웜** 파스텔(노랑·주황·분홍) 위주라 **쿨 계열이 바와 확실히 갈린다**
  // (웜 틴트를 쓰면 툴팁이 "또 하나의 바"처럼 보인다). 대비 실측: 카드 1.11 / 코드 12.01 / 라벨 5.15 / 지연 4.89.
  // ⚠️ 13%를 넘기지 말 것 — 라벨·지연 텍스트 대비가 AA(4.5) 하한에 붙는다. 시안: prototypes/gantt-tooltip-band-color.html
  tipBandBg: "#e8f5f7",
  // 라벨 색은 MUI `text.secondary`(→ #666, 대비 5.7)를 그대로 쓴다. `cellFgDim`(#64757f)은 계열은
  // 맞지만 밴드 음영 위에서 4.33으로 **AA(4.5) 미달**이라 쓰지 않는다.
  tipValueFg: "#22313a",                // = cellFg. 캔버스 셀·바 텍스트와 같은 계열

  // ── 툴팁 카드 셸 ──────────────────────────────────────────────────────────
  // MUI `Paper elevation={4}`만 쓰면 **흰 배경 위에서 카드 경계가 사라진다** — 그림자 번짐이 4~10px로
  // 좁고 최대 불투명도가 0.2라, 간트 캔버스(색 바·격자) 위에서는 떠 보이지만 흰 배경 그리드
  // (AdjustmentGrid) 위에서는 보이지 않는다(2026-08-25 확인). 배경과 무관하게 경계가 서도록
  // **앱의 팝업 셸 표준**(index.js의 `MuiPopover`/`MuiMenu` 오버라이드와 같은 값)을 쓴다.
  // 툴팁을 쓰는 화면이 늘어도 같은 값을 참조하게 여기 둔다.
  tipCardRadius: 2,                                    // = 8px (theme.shape 단위)
  tipCardBorder: "0.5px solid rgba(0, 0, 0, 0.12)",
  tipCardShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
  // AG Grid 셀 툴팁(AdjustmentGrid)용 — **같은 그림자를 `filter`로 그린 것**이다.
  // 커스텀 툴팁은 AG Grid React가 만든 래퍼 div(`.ag-react-container.ag-tooltip-custom.ag-popup-child`)
  // 안에 렌더되고, **그 래퍼에 걸어야만** 그림자가 화면에 나온다 — 안쪽 카드에 box-shadow나 filter를
  // 걸면 둘 다 나오지 않는다(2026-08-25 DevTools 실측). 간트 툴팁은 MUI Popper가 body에 직접 포털로
  // 붙어 이 래퍼가 없어 `tipCardShadow`(box-shadow)가 그대로 동작한다.
  // 거는 위치는 AdjustmentGrid의 그리드 래퍼 sx(`& .ag-tooltip-custom`)다.
  // **위 tipCardShadow와 같은 모양으로 유지할 것** — drop-shadow는 spread를 지원하지 않아 x/y/blur만 옮겼다.
  tipCardDropShadow: "drop-shadow(0 8px 24px rgba(0, 0, 0, 0.12))"
};

// 상태색(지연/준수) — **텍스트용과 도형용을 구분한다.**
// `LATE_MARK_COLOR`(#e03131)는 캔버스 삼각형 마크용이다. 도형은 대비 기준이 텍스트보다 느슨하지만,
// 그 색을 툴팁 글자·뱃지에 쓰면 뱃지 배경 위에서 4.08로 AA 미달이 된다. 그래서 텍스트는 한 단 진한
// 색을 따로 둔다(2026-08-21 대비 측정: 흰 5.46 / 밴드 5.13 / 뱃지 4.93 — 전부 통과).
export const LATE_TEXT_COLOR = "#c92a2a";
export const LATE_TEXT_BG = "#fff5f5";      // 뱃지 배경(붉은 계열 — 글자색과 같은 색조)
export const ONTIME_TEXT_COLOR = "#0b7a48"; // 흰 5.40 / 밴드 5.07
export const ONTIME_TEXT_BG = "#eaf6ef";    // 4.86
// 주의(초과 직전 = Tool "한계") — 지연(빨강)과 준수(초록) 사이 단계. 이전 값 `#ffa94d`는 흰 배경
// 대비 **1.9로 사실상 안 읽혔다**(2026-08-21 점검에서 발견, 툴팁 개편 때 정리 예정으로 TODO 등재).
// amber를 한참 진하게 내려 흰 5.05 / 밴드 4.53으로 둘 다 AA를 넘긴다.
export const CAUTION_TEXT_COLOR = "#b35309";

// 바 —  색은 서버가 행마다 내려주는 displayColor를 쓰고, 여기 값은 그 외 장식용.
// 바 두께는 `LANE_BAR_PADDING_Y`(행 위아래 여백)로 정한다 — 자체 프레임은 행 높이가 겹침 수에 따라
// 가변이라 "행 높이 × 비율" 방식(AG Grid판 `BAR_HEIGHT_RATIO`)을 쓸 수 없다.
// 라운드 — 생산계획은 바가 짧고 촘촘해서(자원 간트는 폭 6~12px가 흔하다) 4px만 넘어가도 캡슐처럼
// 뭉개진다(§7-2a ③-a). 그래서 오래 2px이었는데, **§7-28(맞닿은 쪽 코너 각지게) 이후 올렸다**
// (2026-08-28): 확대해서 보면 2px은 사실상 각져 보였고, 노치가 사라진 지금은 반경을 키워도
// 접합부가 벌어지지 않는다(노치는 §7-28 이전에는 `2×radius`였다). 3px을 거쳐 **2.5px로 정착**.
//
// **소수점을 써도 된다.** 캔버스는 좌표를 정수로 스냅하지 않고 안티앨리어싱으로 렌더하므로
// 2.5는 2·3과 실제로 다르게 그려진다(호가 그리는 넓이가 다르다). 다만 차이는 **곡선의 계조**이지
// 형태가 아니라, 2.5와 3의 구별은 확대해야 보이는 수준이다.
// 반경이 소수여도 §7-26의 동심 보정은 그대로 성립한다 — 스트로크 경로가 `radius - inset`
// (= 2.0)이라 채움 코너 중심과 정확히 일치한다.
//
// ⚠️ **4px 이상으로는 올리지 말 것.** 이유가 둘이다 —
//   ① 위의 캡슐화(폭이 `2×radius` 미만이면 `width/2`로 클램프돼 알약이 된다). 2.5px이면 5px 미만,
//      즉 슬리버만 해당하지만 4px이면 8px 미만 — 줌 아웃에서 흔한 폭까지 들어온다.
//   ② §7-28의 "연속 구간이 한 덩어리로 보인다"는 인상이 **반경에 비례**한다. 3px까지는 거의
//      드러나지 않지만 그 이상이면 서로 무관한 작업들이 묶여 보인다는 지적이 나올 수 있다.
// 행 높이는 20~76px로 조절되며(`ROW_HEIGHT_MIN/MAX`), 최소 20px일 때 바 높이가 10px이라
// 2.5px은 그 25%다 — `barHeight/2` 클램프에는 안 걸리지만 가장 둥글어 보이는 조건이다.
export const BAR_RADIUS = 2.5;
export const BAR_DEFAULT_COLOR = "#c9d6e3";
// 테두리는 유지하되 **검정 18%** 로 낮춘다. 없애면 연한 displayColor가 흰 배경·휴무일 음영과 만나
// 경계가 사라지고, old의 31%는 촘촘한 화면에서 격자처럼 무겁다(§7-2a ③-c).
export const BAR_BORDER_COLOR = "rgba(15, 23, 42, 0.18)";
// 바 라벨 글자 크기 — **그리드 표준(ag-theme-balham `--ag-font-size`)과 같은 12px**로 맞춘다.
export const BAR_FONT = "12px sans-serif";
// 라벨을 놓을 세로 공간이 이보다 좁으면(px) 글자 아래가 잘린다 → 라벨을 생략한다.
export const BAR_LABEL_MIN_HEIGHT = 12;

// 주문 바에 하단 레일을 붙일 최소 폭. 이보다 좁으면 레일 없이 일반 사각형으로 그린다
// (좁은 바에 레일까지 얹으면 색 블록이 두 겹으로 뭉개진다).
export const ORDER_BAR_SHAPE_MIN_WIDTH = 20;
// 주문(요약) 바 하단에 깔리는 **레일**. old의 "밑변 뾰족한 실루엣"을 대각선 없이 대체한 것이다 —
// 대각선은 캔버스에서 안티앨리어싱으로 번져 여러 행이 쌓이면 지저분해 보인다. 레일은 축에 나란한
// 사각형이라 항상 선명하고, "이 바는 하위를 요약한다"는 신호는 그대로 남는다
// (Bryntum/dhtmlx의 parent bar도 같은 계열의 표현이다).
//
// 두께는 **비율(22%)에서 얇은 고정값 계열로 바꿨다**(2026-08-19 사용자 조정안): 기본 바 22px에서
// 22%면 5px이라 라벨이 놓일 밴드가 17px밖에 안 남아 답답했다. 3px이면 밴드가 19px로 늘고, 어두운
// 면적 자체도 40% 줄어 시각적 압박이 크게 준다. 세로 줌으로 바가 커질 때만 비율로 따라 올린다.
export const ORDER_BAR_RAIL_RATIO = 0.12;
export const ORDER_BAR_RAIL_MIN = 3;
export const ORDER_BAR_RAIL_MAX = 6;
export const ORDER_BAR_BORDER_COLOR = "rgba(15, 23, 42, 0.22)";  // 주문 바 테두리(작업 바보다 살짝 진하게)

// hover 강조 — old는 검정 15% 오버레이(`.BGGanttNoneOutHover`, BasicGantt.css:1055)였고, new는
// **밝기 필터**로 간다(색조 유지 + 어두운 displayColor에서도 반응이 보인다).
// DOM(OrderGantt)은 gantt.scss의 `filter: brightness(0.86) saturate(1.1)`로 처리하고, 캔버스
// (ResourceGantt/AdjustmentGantt)는 filter를 쓸 수 없어 아래 값으로 같은 그림을 만든다 —
// brightness(0.86) = 각 채널 × 0.86 = **검정 14% 합성**과 동일하다(saturate 보정만 생략).
// 두 값은 세트다. 한쪽만 고치면 화면끼리 hover 반응이 달라진다.
export const BAR_HOVER_SHADE = "rgba(0, 0, 0, 0.14)";

// 헤더/격자 — **날짜 경계는 무조건 보여야 한다**(사용자 제약). 그래서 격자를 지우는 대신 3단
// 위계를 준다: 주/월 경계(앵커) > 일 경계 > 시간 격자. 앵커가 있으면 일선을 옅게 둬도 판독이 되고
// 화면 소음은 크게 줄어든다(§7-2a ①).
export const TIMELINE_GRIDLINE_COLOR = "rgba(15, 23, 42, 0.11)";   // 일 경계(필수)
export const TIMELINE_WEEKLINE_COLOR = "rgba(15, 23, 42, 0.22)";   // 주/월 경계 = 판독 앵커
export const TIMELINE_SUBLINE_COLOR = "rgba(15, 23, 42, 0.05)";    // 하위(시간) 격자 — Hour 버킷에서만
export const WEEKEND_BG = "rgba(15, 23, 42, 0.028)";
// 주말로 볼 요일(0=일 … 6=토). **프로젝트마다 다를 수 있으니 바꿀 자리를 한 곳으로 모은다.**
// ⚠️ 마스터의 `FP_STANDARD_START_DAY`("주 시작 요일")를 여기 쓰면 안 된다 — 주 시작 요일 ≠ 휴무 요일이다
// (월요일 시작이어도 휴무는 토·일). 실제 휴무는 캘린더 데이터가 담당하고(§7-6), 이 값은 데이터가 없을 때의
// 요일 기반 보조 표시다.
export const WEEKEND_DAYS = [0, 6];
// 밴드가 이보다 좁아지면 그리지 않는다 — 월 버킷을 크게 축소하면 1~2px 줄이 7일마다 반복돼 격자와 섞인다.
export const WEEKEND_MIN_BAND_PX = 3;

// 의존선(작업 순서 화살표) — old는 GanttDependencyColor:'0'(의존선별 색상 미사용)이라 단색이다.
// 색색의 바 위를 지나가도 보이도록 흰 헤일로 + 진한 본선 2중 스트로크로 그린다
// (MP resource-gantt 캔버스의 SELECTION_HALO/OUTLINE 기법과 같은 이유).
// 색: 회색 30%대로 **옅게**(사용자 결정) — 진한 남색은 고채도 바 사이에서 화면을 지배한다.
// 대신 흰 헤일로는 **필수**다: 옅은 선이 고채도 바 위를 지나면 헤일로 없이는 그냥 사라진다(§7-2a ④).
export const DEP_LINE_COLOR = "#9fb0bd";
export const DEP_HALO_COLOR = "#ffffff";
export const DEP_LINE_WIDTH = 1;
export const DEP_HALO_WIDTH = 3.5;      // = 본선 1px + 좌우 헤일로 2.5px

// ── 강조(focus) 톤 — 화면 맥락이 다르면 세기도 달라야 한다(2026-08-19 사용자 확인) ──────────
// 주문 간트에서 의존선은 "행을 펼치면 항상 보이는 배경 정보"(ambient)라 옅은 게 맞다. 반면 자원
// 간트는 **사용자가 바를 더블클릭해 명시적으로 요청한 결과**이고, 경로가 수백 개 고채도 바 사이를
// 5~20행 관통한다 — 옅은 1px로는 배경에 묻힌다. 요청해서 띄운 것이 배경보다 약하면 안 된다.
// (`DEP_LINE_COLOR`를 바꾸는 게 아니라 **맥락별로 나누는** 것이다 — 주문 간트는 그대로 옅게 간다.)
export const DEP_FOCUS_LINE_COLOR = "#4a5b66";
export const DEP_FOCUS_LINE_WIDTH = 1.6;
export const DEP_FOCUS_HALO_WIDTH = 5;
export const DEP_ARROW_SIZE = 5;
export const DEP_ELBOW_MARGIN = 10;
// 목표 바 왼쪽에서 수평으로 진입할 때의 접근 구간(px) — 이 길이만큼 왼쪽에서 꺾어 들어온다.
// 조금 길게 잡으면(=10보다 큼) 맞붙은 작업 사이에서도 화살표 방향이 또렷하게 읽힌다.
export const DEP_ENTRY_STUB = 14;
// 의존선 그리기 방식 — **이 값 하나로 전환한다**(2026-08-19 사용자 결정: 우선 "curve"로 쓰면서
// 역방향/간격 0 케이스를 실화면으로 확인한 뒤 조정). 라우팅 좌표 계산은 방식과 무관하게 동일하다.
//   "curve"  : 제어점을 양 끝에서 수평으로 둔 S커브 하나. 배경(전부 사각형)에 없는 형태라 눈이 선을
//              먼저 분리해낸다. **단, 간격 ≤ 0(역방향)에서는 곡선이 되접혀 루프가 생긴다**(§7-2h-2)
//   "hybrid" : 정방향은 곡선, 간격 ≤ DEP_WRAP_GAP_THRESHOLD(역방향 포함)는 라운드 직각 — 위 루프를 피한다
//   "elbow"  : 전부 라운드 직각(이전 적용본)
// 비교 시안: `prototypes/gantt-dependency-curve-styles.html`(스택), `…-curves.html`(시나리오 매트릭스).
export const DEP_LINE_STYLE = "curve";

// 꺾이는 지점의 원호 반경(px) — "elbow"/"hybrid"에서 쓴다. 6px은 자원 간트의 촘촘한 구간에서
// 코너끼리 먹지 않는 상한선이다. 실제 반경은 코너마다 인접 선분 길이의 절반으로 깎인다(클램프).
export const DEP_CORNER_RADIUS = 6;

// 곡선 제어점 거리(k). **양 끝에서 수평으로** 두는 게 핵심 — 출발·도착 접선이 수평이라 "목표 왼쪽에서
// 수평 진입"(§E-4) 규칙이 곡선에서도 지켜지고 화살촉 방향이 살아 있다.
// k = clamp(가로 간격 × 0.5, MIN, MAX). 상한이 없으면 간격이 넓을 때 곡선이 늘어져 옆 행을 침범한다.
// **역방향 케이스의 루프 크기도 이 두 값이 지배한다** — 조정 시 첫 번째 손잡이.
export const DEP_CURVE_K_MIN = 16;
export const DEP_CURVE_K_MAX = 48;
// 이 픽셀보다 간격이 좁으면(0 이하·역방향 포함) **우회 라우팅**으로 그린다 —
// 오른쪽으로 잠깐 뺀 뒤 행 사이 여백을 지나 목표 왼쪽으로 감아 들어오는 형태.
// 연속 공정은 선행 종료 == 후속 시작이라 이 케이스가 가장 흔하다.
export const DEP_WRAP_GAP_THRESHOLD = 4;

// ── 자원 레인(행당 바 수백 개) 전용 — ganttDraw / ResourceGantt ──
// 자원 한 행에 활동이 수백 개라 DOM 노드로는 폭발한다 → 캔버스에 직접 그린다
// (reports/gantt-implementation.md §5-2a D2). 주문 간트도 같은 캔버스 렌더러를 쓴다(§7-1).
// 색값은 old TreeGrid CSS(`resourcegantt.css` / `treeGrid/Styles/BasicGantt.css`)에서 그대로 가져왔다.

// 검색(주문/품목) 미일치 활동 — old `OnGetGanttRunText`는 `opacity:0.1`이었다.
// **0.18로 올렸다**(§7-8): 0.1은 실화면에서 "데이터가 사라진 것"처럼 보여서 일치 작업 주변의 문맥
// (앞뒤 작업이 뭐였는지, 자원이 비었는지)을 읽을 수 없었다. 0.18이면 형태는 남고 대비는 충분하다.
export const LANE_DIM_ALPHA = 0.18;

// 다운타임 — 행 배경(old `.BGGanttBackRow.BGGanttBack20`)과 분할 다운타임 마크(old `.BGGanttMark2x`,
// 테두리 있음 / `.BGGanttMark3x`, 테두리 없음). old는 행 높이에 따라 클래스 번호를 8단계로 시프트해
// 밴드 높이를 바꿨는데, 캔버스에서는 클래스 체계가 필요 없어 **행 높이 비례 + 테두리 유무**로
// 재정의했다(§5-2a F5).
// 배경 음영이 **4종**(주말 / 휴무일 / frozen zone / 자원 다운타임)이 되므로 색으로 구분하면 고채도
// 바와 경쟁한다. 전부 **무채색 명도 차 + 패턴**으로 가른다(§7-2a ②). old의 노랑 다운타임(#ffffcc)은
// 파스텔·고채도 바 양쪽과 충돌해 폐기했다.
// ── 비가동(휴무) 표시 ─────────────────────────────────────────────────────
// **휴무와 divideY는 다른 개념이 아니다.** 휴무는 그 자원이 일하지 않는 구간이고, divideY는
// **작업이 그 휴무 구간을 점유하지만 작업 상태는 아닌 것**(휴무 전후로 이어지는 하나의 작업)이다.
// 그래서 시각 언어를 하나로 묶는다 — **사선의 색·간격을 두 곳이 공유**하고, 바 위에 얹힐 때만
// 밝은 면으로 뒤집는다(검정 알파는 채도 높은 바 위에서 보이지 않는다).
// 시안 `prototypes/gantt-downtime-styles.html` D안(2026-08-24 사용자 선택), 경과는 §7-20a.
//
// **세기를 올렸다**(0.04/0.11 → 0.09/0.16). 옛 값은 주말 밴드(`WEEKEND_BG` 2.8%)와 명도 차가
// 거의 없어 **휴무인지 주말인지 구분되지 않았다** — 휴무는 계획을 읽는 데 중요한 정보다.
export const DOWNTIME_BG_COLOR = "rgba(15, 23, 42, 0.09)";
export const DOWNTIME_HATCH_COLOR = "rgba(15, 23, 42, 0.16)";      // 45° 사선(촘촘). divideY와 공유
// **구간 양끝 세로 경계선.** 가시성에 가장 크게 기여하는 요소다 — 배경 알파를 더 올리는 것보다
// "여기서 시작하고 여기서 끝난다"를 못 박는 편이 훨씬 잘 읽힌다(밴드가 옅어도 경계가 있으면 읽힌다).
export const DOWNTIME_EDGE_COLOR = "rgba(15, 23, 42, 0.40)";

// divideY 마크(바 **위에** 얹히는 것)의 면 — **불투명**이다.
// 0.70 → 0.35(반투명)로 낮춘 적이 있는데, 반투명은 "면"이 아니라 "필터"처럼 보여 바 색을
// 오염시킨다. 아래 `DIVIDE_MARK_HEIGHT_RATIO`의 하단 레일이 연속성을 맡으므로 면은 불투명해도 된다.
export const DIVIDE_MARK_FILL = "#f2f5f8";
// **위쪽 정렬 + 하단은 바 색 레일**(old `.BGGanttJC…`가 아니라 old 조정간트의 "하단 띄움" 표현).
// 바 높이의 이 비율만 마크가 덮고 **남은 아래쪽은 바 자기 색이 그대로 보인다** → "이 바가 아래에서
// 계속 이어진다"(= 점유하지만 작업 아님)가 형태로 읽힌다. 중앙 정렬은 위아래로 바 색이 갈려
// "관통하는 줄무늬"로 보여 연속성이 아니라 손상처럼 읽혔다.
export const DIVIDE_MARK_HEIGHT_RATIO = 0.72;
export const DIVIDE_MARK_MIN_RAIL = 2;      // 행 높이를 최소로 줄여도 레일이 사라지지 않게
// JC 띠 전용 비율 — 띠가 작업 바의 절반 높이(`JC_BAR_HEIGHT_RATIO`)라 같은 0.72 를 쓰면 남는
// 레일이 3px 뿐이라 "이 띠가 이어진다"가 안 읽힌다. **얇은 바일수록 레일 몫을 키운다**(§7-46).
// 행 32 기준: 띠 11px → 마크 7px + 레일 4px.
export const DIVIDE_MARK_JC_HEIGHT_RATIO = 0.6;
export const DOWNTIME_MARK_BORDER_COLOR = "rgba(15, 23, 42, 0.35)";  // 분할 다운타임 마크 테두리

// 휴무일(비가동일) — 주말보다 강하고 다운타임보다 약한 45° 사선. **데이터 연결은 아직 없다**(§7-2b).
// (옛 `HOLIDAY_BG_COLOR`/`HOLIDAY_HATCH_COLOR` 제거 — 전역 휴일 밴드 자체를 걷어냈다, §7-21)

// Frozen zone(계획 시작 ~ freeze 시각) — 그 구간은 확정되어 변경 불가라는 표시다.
// 값은 사용자가 버전 편집 폼에서 지정한 `freezeTs`이고(엔진 계산값이 아니다), 간트는 버전 드롭다운이
// 이미 받아 둔 `selectedVersion.freezeTs`를 꺼내 쓴다(별도 API 호출 없음).
//
// **teal → 연녹으로 바꿨다**(2026-08-24). 두 가지가 걸렸다:
//   ① `headerAccent`(#048ba8, 같은 teal)는 **상호작용·상태 전용**이라고 이 파일에 못 박아 뒀는데
//      (헤더 하단 2px 액센트를 걷어낸 근거, §7-2j) 동결 구간은 **항상 켜져 있는 구조적 밴드**다.
//      규칙을 어기면 teal의 의미가 닳고 검색 배지·포커스 링과 경쟁한다.
//   ② old도 teal이 아니라 연녹 계열(`.BGGanttBack11` = `#f0feee`)이었다 — 휴무(`#20` = `#ffffcc` 노랑)와
//      **다른 색**을 준 것이 옳았다. 동결(시간 확정)과 휴무(비가동)는 성격이 완전히 다른 정보라
//      무채로 뭉치면 구분 비용이 커진다.
//
// ⚠️ **휴무보다 약하게 유지한다**(2026-08-24 사용자 지시: "동결 구간보다 휴무가 더 위/우선순위 높게").
// 그래서 휴무처럼 양끝 실선 경계를 주지 않고 **freeze 시각 쪽 점선 하나**만 둔다 — 시작은 대개 축
// 시작(계획 시작)과 같아 선이 중복되기도 한다. z-순서도 이미 휴무가 위다(동결=배경 층, 휴무=행 층).
export const FROZEN_ZONE_COLOR = "rgba(16, 138, 90, 0.07)";
export const FROZEN_ZONE_BORDER_COLOR = "rgba(16, 138, 90, 0.35)";

// 결품(shtgYn) 마일스톤 — old `.BGGanttShtgMilestone`(18px 주황 경고 아이콘)을 같은 색 마름모로.
export const SHORTAGE_MARK_COLOR = "#f76707";
export const SHORTAGE_MARK_SIZE = 14;

// ── 상태 마크 — 좌상단 Tool 사용 / 우상단 납기지연 ─────────────────────────────
// **모서리 접힘(fold) + 흰 케이싱**(2026-08-21 사용자 선택, 시안 `prototypes/gantt-bar-marks.html` H안).
// "잘 안 띈다"의 원인은 크기가 아니라 **배경색에 따라 대비가 달라지는 것**이었다 — displayColor
// 상당수가 명도 0.9 이상이라 회색·빨강 삼각이 묻혔다. 삼각의 사면에 흰 띠를 깔면 어떤 채움색
// 위에서도 마크가 떨어져 나오고(의존선·선택 링이 쓰는 것과 같은 기법) "접힌 모서리"로 읽힌다.
//
// **케이싱이 대비를 담당하므로 크기를 키우지 않는다** — 지연은 자원 간트에서 흔해서(절반 가까이)
// 키우면 "예외 신호가 배경 패턴"이 된다(§7-2c ④에서 8×8 2색 → 5px 단색으로 낮춘 이력).
// 조정 손잡이: 시끄러우면 `CORNER_MARK_SIZE`를, 안 보이면 `MARK_CASING_WIDTH`를 만진다.
export const MARK_CASING_COLOR = "rgba(255, 255, 255, 0.92)";
export const MARK_CASING_WIDTH = 1.5;

// **두 마크는 같은 크기다**(2026-08-21 사용자 지시). 처음엔 "지연은 흔하니 한 단 작게"로 갈랐는데,
// 좌·우 코너에 크기가 다른 삼각이 앉으면 **바가 기울어 보이고** 둘이 같은 종류의 신호로 읽히지
// 않는다. 구분은 크기가 아니라 **위치(좌=Tool / 우=지연)와 색**이 담당한다.
// 값 8 = old `.lateYnMark`의 8×8과 같다(형태만 2색 사각 → 단색 삼각으로 바뀐 것).
export const CORNER_MARK_SIZE = 8;

// old `.lateYnMark`는 바 우측 상단 8×8(좌 노랑 / 우 빨강 4×8 두 칸)이었다 — 위 이유로 단색 삼각이다.
export const LATE_MARK_COLOR = "#e03131";

// Tool 사용 마크 — old `.toolStatusDot`(바 좌측 상단 11px 직각 삼각형).
//
// **명암을 뒤집었다**(2026-08-24 사용자 지시): 어두운 회색 삼각 + 흰 케이싱 → **흰 삼각**.
// 지연 마크(빨강)와 **같은 방향으로 어두웠던 것**이 문제였다 — 둘 다 "진한 삼각"이라 색만으로
// 구분해야 했다. 흰 면으로 뒤집으면 **명암 자체가 두 마크를 가른다**(밝은 삼각 = Tool / 진한 삼각 = 지연).
//
// **대비는 케이싱이 아니라 윤곽선이 만든다**(2026-08-24 2차, 시안 `prototypes/gantt-tool-mark-outline.html`
// C안). 처음엔 지연 마크와 같이 케이싱(사면 띠)을 썼는데 **옅은 바 위에서 안 보였다** — 케이싱은
// 같은 꼭짓점의 더 큰 삼각을 뒤에 까는 방식이라 **차이가 드러나는 곳이 빗변뿐이고, 위 변과 왼쪽 변은
// 흰 면이 바 색과 그대로 맞닿는다.** 빨강은 면 자체가 어떤 파스텔과도 대비가 나와 이 구조가 문제가
// 안 됐지만 흰 면은 두 변이 사라져 "모서리가 살짝 밝다"로만 읽혔다. 케이싱을 진하게 해도 **위치가
// 그대로라** 해결되지 않는다 → **세 변을 모두 확정하는 윤곽선**으로 바꿨다.
// 케이싱을 빼서 총 두께는 늘지 않는다(§7-11의 "크기를 키우지 않는다" 제약 유지).
// 0.55가 상한이다 — 더 진하면 지연 마크(경고)보다 무거워져 위계가 뒤집힌다.
export const TOOL_MARK_COLOR = "#ffffff";
export const TOOL_MARK_OUTLINE_COLOR = "rgba(15, 23, 42, 0.55)";

// 선택 강조 — **바 바깥에** 케이싱(배경색) + 링(선택색)을 두 겹 두른다.
//
// 2026-08-21 개편: 이전에는 `strokeRect(x+1.5, y+1.5, w-3, h-3)`으로 **바 안쪽에** 그렸다. 두 문제가
// 겹쳐 있었다 — ① 3px 흰 헤일로가 색 면을 안쪽에서 먹어 22px 바가 16px로 보이고 짧은 바(8px)는 색이
// 거의 사라졌다("선택하면 갑자기 작아진다"), ② 헤일로와 외곽선을 **같은 사각형**에 그려 헤일로가 덮여
// 얇은 안쪽 선 하나만 남아 어두운 바에서 안 보였다.
// 지금은 CSS `box-shadow: 0 0 0 1.5px bg, 0 0 0 3.5px color`와 같은 그림을 캔버스로 만든다:
// 바깥 0~1.5px = 케이싱(배경색), 1.5~3.5px = 링(선택색). **채움 영역을 건드리지 않아 크기가 그대로다.**
// 케이싱이 한 겹 띄워 주므로 어두운 계열 바나 이웃 바와 맞붙어도 링이 뭉치지 않는다.
// 시안: prototypes/gantt-bar-selection.html, 결정 경과: gantt-implementation.md §7-9.
export const LANE_SELECTION_CASING = "#ffffff";       // 케이싱 = 캔버스 배경색
export const LANE_SELECTION_CASING_WIDTH = 1.5;
export const LANE_SELECTION_RING = "#102a43";         // 링 — 무채 네이비(무지개색 바 어디에도 붙지 않는다)
export const LANE_SELECTION_RING_WIDTH = 2;

// ── 작업 교체(JC) 박스 ─────────────────────────────────────────────────────
// old는 JC를 **작업 박스와 다른 두께**로 그렸다 — `OnGetGanttRunClass`가 `JC`/`BlurJC` 클래스를
// 돌려주고, `BasicGantt.css:1018`의 `.BGGanttJCIn > div { height: 50% }` + 컨테이너
// `align-items: center`가 **바 높이의 절반짜리 띠를 세로 중앙에** 놓았다. 이관 때 이 규칙이 빠져
// 작업 박스와 같은 두께로 그려지고 있었다(2026-08-24 사용자 지시로 복원, §7-17).
// 두께로 구분되면 "이건 실제 작업이 아니라 준비시간"이 색과 무관하게 읽힌다.
export const JC_BAR_HEIGHT_RATIO = 0.5;
export const JC_BAR_MIN_HEIGHT = 3;         // 행 높이를 최소로 줄여도 띠가 사라지지 않게
// ⚠️ **선택 시 띠 두께를 바꾸지 않는다.** old에는 `.BGGanttSelectedOut.BGGanttJCOut > div > div
// { height: calc(50% + 2px) }`(BasicGantt.css:1029)가 있어 2px 두꺼워졌지만, 2026-08-24 사용자
// 지시로 **원래 높이를 유지**한다 — 선택 표현은 링이 전담하고(§7-18a에서 링 모양도 JC 유무와
// 무관하게 통일했다) 띠까지 같이 변하면 "선택하면 모양이 달라지는" 상태가 다시 생긴다.

// **prev(이 작업 전) / next(이 작업 후)를 색으로 구분한다.**
// 백엔드가 이미 `ResourceGanttService.java:35-36`에서 `#000000`(prev) / `#808080`(next)를
// `displayColor`에 실어 보낸다. 값을 여기로 옮겨 온 이유:
//   ① 이 파일이 간트 색의 단일 출처다(테마 교체 가능성 유지) — 바 채움색만 데이터에서 오고,
//      JC 색은 데이터가 아니라 **고정 UI 규약**이다.
//   ② `saas-fp`는 읽기 전용이라 순수 검정(#000000)을 조정할 수 없다. 순수 검정은 행 격자·주말
//      배경 위에서 구멍처럼 보여서 한 단 올린 무채 슬레이트를 쓴다.
// prev가 더 진한 old의 관계(검정 > 회색)는 그대로 유지한다.
export const JC_PREV_COLOR = "#343a40";     // 이 작업 **전** 교체 — 진한 무채
// ⚠️ **gray-500 → gray-600 으로 한 단 내렸다**(2026-09-15 신고 "next jc 색상이 조금 연하다").
// old 는 `#808080`(`AdjustmentGanttService:30`)인데 gray-500 은 그보다 **훨씬 연했다** —
// 흰 배경 대비 3.95:1 → 2.07:1. 관계(prev 가 더 진하다)만 지키고 값은 한쪽만 크게 옮겼던 것이다.
// gray-600 은 4.69:1 로 old 보다 오히려 약간 진하다. 빗금 마크(`#f2f5f8`) 대비도 1.90 → 4.29:1 —
// 밝은 띠 위에서 빗금이 안 보이던 문제까지 같이 풀린다(§7-46 ③).
export const JC_NEXT_COLOR = "#6c757d";     // 이 작업 **후** 교체 — 밝은 무채

// ── 떨어진 JC 연결선 (§7-40 A안) ──────────────────────────────────────────────
// 엔진은 JC 를 활동에 붙이지 못할 때가 있다(§7-40 원인 ①~⑤ — 분할타입·휴무 조합).
// 그때 검정 띠만 덩그러니 남으면 **어느 작업의 것인지 알 수 없다.** 틈을 파선으로 잇는다.
// **떨어졌을 때만 그린다** — 붙어 있으면 그릴 자리도 없고 잉크만 는다(2026-09-11 결정).
// 틈 자체에 음영을 넣는 C안은 기각했다: 그 구간은 엔진이 JC 용으로 **예약**한 자리라
// (`FreeTimeSectionSupplier.getJcAppliedTimeSection:343`) "비었다"고 말하면 거짓말이 된다.
export const JC_LINK_COLOR = "#8a9299";     // 무채 계열 — 바 색(데이터)과 경쟁하지 않는다
export const JC_LINK_DASH = [3, 3];
export const JC_LINK_CAP_HEIGHT = 9;        // 양 끝 세로 눈금 — 어디서 어디까지인지 못을 박는다
export const JC_LINK_MIN_WIDTH = 3;         // 이보다 좁으면 파선이 점 하나라 안 그린다

// 행 구분선 / 바 두께
export const LANE_ROW_DIVIDER_COLOR = "#eef2f5";
export const LANE_BAR_MIN_WIDTH = 2;
// 행 위아래 여백(px) — 바 높이 = rowHeight - 2*이 값. **3 → 5로 키웠다**(2026-08-19 사용자 검토):
// 자원 간트는 색면이 행을 꽉 채워 행 구분이 사라지고, 주말·휴무일·다운타임 배경까지 전부 덮였다.
// 행당 10px 배경이 노출되면 그 셋이 한 번에 살아난다(기본 행 32px → 바 22px).
export const LANE_BAR_PADDING_Y = 5;
// 라벨을 그리기 시작하는 최소 바 폭.
//
// **34 → 26** (2026-09-11). 신고는 "좁은 작업이 빈 박스처럼 보인다"였는데, 반대 방향 제안도
// 보류로 남아 있었다(§7-2 보류 ③ — "접두사만 남는 라벨은 정보량이 0인데 잉크는 가장 많다").
// 둘은 같은 불만의 양면이다: **좁을 때 라벨이 쓸모가 없다.**
//
// 기준은 "`D…` 한 글자 + 말줄임표가 들어가는 폭"이다. 12px sans 에서 `…` 는 약 1em(12px)이라
// 글자 두 개 값을 먹는다 → `D…`(≈9+12) + 여백 → **약 26px**. 34 는 그보다 8px 높아서
// **`D…` 가 들어갈 수 있는 바에서도 라벨을 안 그리고 있었다.**
//
// 🚫 **이보다 더 내리지 말 것.** 26px 아래는 `…` 하나만 남는데, 그게 §7-2 가 지적한
// "잉크만 늘고 정보 0"의 가장 순수한 형태다. 절벽은 여기 있는 게 맞다.
//
// ⚠️ 머리 말줄임을 **유지**한다(꼬리 말줄임 `…0012` 은 검토 후 기각). `woCd` 가
// `DMND#…` / `SO#…` 두 계열이라 **첫 글자가 주문 종류를 구분**하기 때문이다 —
// 접두사가 하나뿐이라면 정보량이 0이지만, 계열이 둘이면 0이 아니다.
// 시안: prototypes/gantt-narrow-bar-label.html
export const LANE_BAR_TEXT_MIN_WIDTH = 26;
// 좁은 바의 라벨 좌우 여백. 넓은 바는 그대로 두고 여기만 줄여 **임계값을 더 내릴 여유**를 만든다
// (한 글자를 더 벌려는 게 아니다 — 머리 말줄임에서 두 번째 글자는 얻는 게 없다).
// 2px 은 1px 테두리와의 실질 간격이 1px 이라 답답해서 **3** 으로 뒀다.
export const LANE_BAR_TEXT_NARROW_PAD = 3;
export const LANE_BAR_TEXT_NARROW_WIDTH = 40;
// 🚫 **`LANE_BAR_GUTTER`(인접 바 사이 흰 틈)는 폐기했다**(2026-08-28 §7-25).
// 1 → 2px로 올려 가며 경계의 주역으로 삼았지만(§7-2a ③-b, §7-3e G안), 틈은 폭의 **오른쪽에서만**
// 깎여서 바의 종료 시각이 실제보다 이르게 그려졌다. 기본 배율에서 1px ≈ 3.7분이라 그 2px이
// **실제 유휴시간과 구별되지 않았다** — "10분 비는 작업"과 "딱 붙은 작업"이 같은 틈으로 보였다.
// 계획 화면에서 연속 여부는 판독의 핵심이라 시각적 구분보다 우선한다.
// 경계는 이제 테두리(`BAR_EDGE_*`)가 전담한다. 되살리려면 `drawBox`의 `x1` 계산부터 볼 것.

// ── 작업 바 경계 표현 (§7-3e G안 = 폭 적응형 + 세기 완화 + 틈, 2026-08-21 사용자 선택) ──
// 반픽셀 좌표를 정수로 맞춰 경계를 선명하게 만든 뒤(§7-3d) "이제는 너무 뚜렷해서 촌스럽다"는 지적이
// 나왔다. 원인은 **테두리 세기가 바 폭과 무관하게 일정**한 것이었다 — 폭 10~14px 바에 1px 링이 사방으로
// 들어가면 면적의 20%가 선이 되어 스티커처럼 보인다. 시안: prototypes/gantt-bar-edge-styles.html
//
// ── 2026-09-10 (§7-33): **폭에 따른 세기 조절을 완전히 제거했다.** 상수 2개도 같이 지웠다 ──
// 이력(되돌리려는 사람이 읽을 것):
//   ① 16px 임계 → "같은 색인데 테두리가 있는 작업과 없는 작업이 섞여 보인다"(같은 색·비슷한 길이의
//      이웃 14px/18px이 경계를 걸치면 한쪽만 선이 생긴다)
//   ② 6px로 내림 → **줌 아웃(주 버킷)에서 폭이 2~14px에 몰리자 같은 혼재가 재발.** 임계값을 옮기는
//      것으로는 못 고친다 — 불연속 자체가 원인이라 폭이 그 근처에 몰리는 줌 레벨이 항상 존재한다.
//   ③ 폭 3~9px 선형 램프(`BAR_EDGE_FADE_MIN/FULL_WIDTH`) → 불연속은 없앴지만 **연속적인 편차**가
//      남았다. 폭이 섞여 나열되는 실제 화면에서 세기가 0.17~0.83 으로 흩어져
//      **"테두리가 표시됐다 안 됐다 해서 일관성이 없다"**는 신고(2026-09-10).
//   ④ **전강 고정** ← 지금. 편차가 0이라 이 계열 증상이 원천적으로 사라진다.
// 폭 2px 이하는 사방 1px 링에 채움이 다 먹혀 바 전체가 테두리색이 되는데, 그건 `barEdgeOn` =
// **같은 계열의 어두운 색**이라(아래 `BAR_EDGE_STEPS` 주석) 색 정보가 사라지지 않고 어두워질 뿐이다.
// 오히려 옅은 채움(흰 배경 대비 1.1)은 테두리가 없던 ③ 쪽이 더 안 보였다.
// 시안: prototypes/gantt-bar-edge-narrow.html (현행/A/B/D/C 5안 + 슬리버 밀집 행)

// ── 2026-08-21 재검토(H안): 세기를 **채움 밝기에 적응**시킨다 ──────────────────
// G안(고정 0.82 + 반투명 0.5)을 적용한 뒤 "옅은 색 작업이 구분되지 않는다"는 지적이 나왔다.
// 실측으로 원인이 둘 확인됐다(시안: prototypes/gantt-bar-edge-styles.html의 대비 표):
//   ① **alpha의 함정** — 테두리는 `barPath(0.5)` 링이라 **채움 위에** 그려진다. 반투명은 흰 배경이 아니라
//      자기 채움색과 섞이므로 실효 어둡기는 `1 - alpha*(1-darken)` = **0.91**이었다(의도 0.82의 절반).
//      그래서 색과 무관하게 "테두리:채움" 대비가 1.17~1.22로 붙어 사실상 테두리가 없었다.
//   ② **옅은 색은 채움 자체가 안 보인다** — `#fdf6a8`은 흰 배경 대비 1.11, `#ccf0c4`는 1.25다.
//      테두리가 유일한 경계인데 그마저 1.36이면 바가 사라진다. 반대로 고채도 바는 채움만으로 2.5~4.6이라
//      테두리가 약해도 형태가 읽힌다(= G안이 잡으려던 "스티커 느낌"은 고채도에서만 문제였다).
// → **alpha를 없애고**(의도가 값에 그대로 드러난다) 어둡기를 밝기 구간별로 둔다. 구간 함수를 쓴 이유는
//   값을 표로 읽고 조정할 수 있어서다(연속 수식보다 유지보수가 쉽다).
//
// ── 2026-08-28 재조정(§7-27): 하위 두 구간을 `0.80/0.86 → 0.74/0.72`로 ────────────
// 위 H안의 값은 **gutter 2px이 있던 시절**에 정해졌다. 아래 두 구간을 약하게 둔 근거가
// "고채도는 채움만으로 이미 읽힌다"였는데, 그건 **바 vs 배경** 얘기다. 같은 색 바가 맞닿는
// 경우는 흰 틈이 대신 처리해 줬고, §7-25로 그 틈을 없애면서 논리에 구멍이 생겼다 —
// **같은 색 인접 시 테두리가 유일한 구분 수단인데** 실측 대비가 1.29(DEFAULT)~1.54까지 떨어졌다.
// 하위 8색만 1.68~1.77로 끌어올린다(옅은 13색은 픽셀 단위로 동일 — 구간이 안 바뀐다).
// 최대치 2.52는 그대로라 "옅을수록 강한 테두리"라는 위계는 유지되고, 전체 편차는 1.96 → 1.50배로 준다.
// ⚠️ "강한 선 = 스티커" 우려는 **alpha 0.5를 쓰던 상태**의 관찰이다(실효 어둡기 0.91로 의도의 절반이라
//    값과 결과가 어긋나 있었다). 지금은 alpha가 없어 값이 그대로 나온다.
// 시안: prototypes/gantt-bar-edge-steps-tune.html (색별 최악 케이스 + darken 슬라이더)
export const BAR_EDGE_STEPS = [
  { minLum: 0.86, darken: 0.64 },   // 연노랑·연녹 — 채움:흰배경 1.1~1.3 → 테두리가 유일한 경계
  { minLum: 0.72, darken: 0.72 },
  { minLum: 0.56, darken: 0.74 },
  { minLum: 0.35, darken: 0.72 }    // 고채도 — 배경과는 충분하나 **같은 색끼리 맞닿을 때**가 기준
];
// 이보다 어두운 채움은 어둡게가 아니라 **흰쪽으로 밝혀야** 테두리가 보인다.
export const BAR_EDGE_LIGHTEN = 0.42;
// (옛 `BAR_EDGE_PALE_LUM` / `LANE_BAR_EDGE_PALE_MIN_WIDTH` 제거) — 임계를 색별로 나눴더니 **눈으로
// 구분되지 않는 두 색이 경계를 걸치는** 문제가 생겼다: `#f7b1b1`(lum 0.776)은 16px, `#fcd5e4`(0.888)은
// 6px이라 비슷한 분홍끼리 테두리 유무가 갈렸다. 임계를 6px 하나로 통일해 분기를 없앴다.
// **`barBorderOn`(주문 바 테두리·하단 레일)의 0.62는 그대로 둔다** — 레일은 §7-2f에서 따로 맞춘
// 값이고, 요약 바가 작업 바보다 경계가 강한 것이 위계상 맞다.

// ── 보정(편집) 레이어 — AdjustmentGantt 전용 (§6-5a / §6-6) ────────────────────
// 드롭 판정 3단계를 **색으로만** 구분한다: 초록=그대로 들어감 / 노랑=뒤가 밀림 / 붉음=불가.
// 신호색이라 바 채움 팔레트(파스텔 무지개)와 겹칠 수 있어 **면은 아주 옅게, 테두리로 말한다**.
export const DROP_OK_FILL = "rgba(16, 138, 90, 0.14)";
export const DROP_OK_EDGE = "rgba(16, 138, 90, 0.75)";
export const DROP_PUSH_FILL = "rgba(217, 141, 12, 0.16)";
export const DROP_PUSH_EDGE = "rgba(217, 141, 12, 0.85)";
export const DROP_DENY_FILL = "rgba(224, 49, 49, 0.12)";
export const DROP_DENY_EDGE = "rgba(224, 49, 49, 0.8)";
export const DROP_BAND_RADIUS = 3;

// **좌드래그(이동) 전용 자리표시**(§7-15h) — 이동은 바를 실제로 옮기지 않고 "놓일 자리"만 파선으로
// 표시한다. old도 이 방식이었다(붉은 파선 placeholder). 밀림 연쇄가 길 때 여러 바가 출렁이지 않고
// **원래 상태가 계속 보여 비교가 된다**. 반대로 Slide·리사이즈는 밀림·길이 변화 자체가 관심사라
// 실이동을 유지한다(§7-15d).
export const DROP_PLACEHOLDER_DASH = [5, 3];
// 삽입 지점 표식 — 자리표시의 좌측 경계(= 놓이는 시작시각)에 세로선 + 위쪽 삼각형.
export const DROP_CARET_SIZE = 5;

// ── 드래그 중 표현 — **실이동**(고스트 아님, 2026-08-28 사용자 결정, §7-15d) ──────────
// 잡은 작업도 밀려나는 이웃도 **실제 바가 그 자리로 움직인다.** 예전에는 커밋 위치에 실바를 두고
// 그 위에 반투명 고스트를 얹었는데(실바 + 고스트 + 밀림고스트 + 밴드 = 4겹), 바가 촘촘한 구간에서
// 고스트와 실바가 겹쳐 **어느 것이 결과인지 읽히지 않았다**(사용자 캡처).
//
// 실이동으로 잃는 것은 "원래 어디였나" 하나뿐이라 그것만 아래 두 토큰으로 보전한다.

// 잡은 작업의 **출발 자리** — 채우지 않고 점선 윤곽만. 얼마나 옮겼는지가 거리로 읽힌다.
export const ORIGIN_OUTLINE_COLOR = "rgba(16, 42, 67, 0.45)";
export const ORIGIN_OUTLINE_DASH = [4, 3];

// **밀려난** 작업 표식 — 위치로 말하던 것(옛 밀림고스트)을 색으로 옮긴 것이다. 좌측 얇은 레일이라
// 바 색을 가리지 않는다. teal(`UI.headerAccent`)은 이 화면에서 상호작용·상태 전용이고,
// "내 조작 때문에 움직였다"는 정확히 상태다.
export const PUSHED_RAIL_COLOR = "#048ba8";
export const PUSHED_RAIL_WIDTH = 3;

// 드래그 팁(old `GanttDragTip:'15'` + `OnGanttDragTip`) — `시작 ~ 종료`를 커서 옆에 띄운다.
export const DRAG_TIP_BG = "rgba(16, 42, 67, 0.92)";
export const DRAG_TIP_COLOR = "#ffffff";
export const DRAG_TIP_FONT = "12px sans-serif";
export const DRAG_TIP_PAD_X = 7;
export const DRAG_TIP_PAD_Y = 5;
export const DRAG_TIP_OFFSET_Y = 18;

// 변경된 작업 표식 — old는 변경 여부를 뷰 상태(`isUpdated`) 하나로만 들고 있어 **어느 바가 바뀌었는지
// 화면에 표시가 없었다.** 저장 경로가 없는 화면이라(§6-1) 무엇을 건드렸는지 보이지 않으면 재조회
// 한 번에 통째로 잃는다 → 바 좌상단에 작은 삼각 마크를 얹는다(납기지연 마크는 우상단이라 겹치지 않는다).
export const CHANGED_MARK_COLOR = "#1971c2";
export const CHANGED_MARK_SIZE = 8;

// ── 툴바 묶음(pill) 공용 스타일 ────────────────────────────────────────────────
// 표시 배율 묶음(`GanttViewControls`)과 보정 조작 묶음(`AdjustmentGantt`의 좌측 툴바)이 **한 줄에
// 나란히** 놓이므로 면·테두리·높이가 같아야 한다. 각자 sx를 적으면 한쪽만 고쳤을 때 계열이 갈린다
// (툴팁을 `GanttTooltip`으로, 상세 행을 `GanttDetailRows`로 합친 것과 같은 이유).
// 담긴 컨트롤이 높이 30이라 위아래 3px 여백이면 36px 캡슐이 된다(조회조건 40px보다 낮아 부수적
// 요소로 읽힌다). 좌우 8px는 첫/끝 아이콘 버튼의 원형 hover가 테두리에 닿지 않는 최소치.
export const CTRL_GROUP_SX = {
  display: "flex",
  alignItems: "center",
  gap: 0.25,
  px: 1,
  py: 0.375,
  bgcolor: UI.ctrlGroupBg,
  border: "1px solid",
  borderColor: UI.ctrlGroupLine,
  borderRadius: 999
};

// 묶음 안에 들어가는 Select — 아이콘 줄과 같은 높이(30px)로 낮추고 라벨은 뗀다. 떠 있는 라벨이 있으면
// 높이가 40px이 되어 아이콘 버튼들과 무게가 안 맞는다. 배경을 흰색으로 못 박는 이유는 묶음 면이
// 깔리면서 투명한 Select가 회색으로 잠겨 **입력 컨트롤로 안 보이게** 되기 때문이다.
export const CTRL_SELECT_SX = {
  height: 30,
  fontSize: 13,
  minWidth: 64,
  bgcolor: "background.paper",
  "& .MuiSelect-select": { py: 0, pl: 1.25, pr: "28px !important" },
  "& .MuiSelect-icon": { right: 4 }
};

// 엣지 드래그 리사이즈 손잡이. 바 양 끝 이 폭 안에 커서가 들어오면 `ew-resize`로 바뀐다.
export const RESIZE_EDGE_PX = 6;
// **손잡이 폭을 바 폭에 비례시킨다 — 최소 폭 임계값을 두지 않는다**(2026-08-28 사용자 결정, §7-15g).
// 옛 구현은 "폭 < 18px이면 엣지 존을 끈다"였는데, 그 18px을 시간으로 환산하면 기본 버킷(일)에서
// 1시간 48분, 주 버킷에서 13시간 45분 미만 작업이 **전부 리사이즈 불가**였다. 비례로 두면 폭과
// 무관하게 hover만으로 손잡이가 잡히고, **이동 영역이 항상 (1 - 2×비율) 남는다**(원래 임계값이
// 있던 이유가 해소된다).
// 0.3 → 이동 영역 40%. 오조작이 잦으면 낮추고(이동 영역↑) 손잡이를 못 잡으면 올린다.
export const RESIZE_EDGE_RATIO = 0.3;

// 경계 **바깥** 손잡이 — 바 끝이 화면에서 1~2px일 때 유일한 조준 수단이다.
// ⚠️ **이웃 몸통 위에서는 쓰지 않는다**(호출부가 판정) — 연속 배치에서는 "바깥"이 곧 이웃의 "안쪽"이라
// 그대로 두면 한 경계에서 판정이 4중으로 겹친다(어느 바의 어느 끝인지 정할 규칙이 없어진다).
export const RESIZE_OUTER_PX = RESIZE_EDGE_PX;

// 드롭 시 **박스 경계 흡착** 허용치(px). 커서가 어떤 작업의 시작/끝에서 이 거리 안이면 그 지점에
// 삽입한다(뒤는 밀린다). 실제 계획 데이터는 선행 종료 == 후속 시작인 연속 배치가 절반 이상이라
// **경계에 0폭 갭밖에 없고**, 이 흡착이 없으면 붙어 있는 작업 사이에는 아무것도 끼워 넣을 수 없다
// (2026-08-28 사용자 지적). 리사이즈 손잡이와 같은 값을 쓴다 — 둘 다 "바 끝을 겨냥한다"는 같은 동작이라
// 허용치가 다르면 손끝 감각이 달라진다.
export const DROP_SEAM_PX = RESIZE_EDGE_PX;
